import React from "react";
import {
  Box,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Chip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const DEFAULT_STATUS = "in-progress";
const DEFAULT_LOCATION = "";

const BookingFilters = ({
  isCompact = false,
  searchRef,
  searchQuery,
  setSearchQuery,
  locationFilter,
  setLocationFilter,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  statusFilter,
  setStatusFilter,
  clearFilters,
  locations = [],
  statuses = [],
  dateOn,
  setDateOn,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fullWidthOnMobile = { xs: "100%", md: "auto" };
  const controlMinWidth = (mdWidth) => ({
    width: fullWidthOnMobile,
    minWidth: { md: mdWidth },
  });

  const hasFilters =
    Boolean(searchQuery) ||
    Boolean(locationFilter) ||
    Boolean(startDate) ||
    Boolean(endDate) ||
    statusFilter !== DEFAULT_STATUS;

  const chips = [];
  if (searchQuery)
    chips.push({
      label: `“${searchQuery}”`,
      onDelete: () => setSearchQuery(""),
    });
  if (locationFilter)
    chips.push({
      label: locationFilter,
      onDelete: () => setLocationFilter(DEFAULT_LOCATION),
    });
  if (startDate || endDate)
    chips.push({
      label: `${startDate || "…"} → ${endDate || "…"}`,
      onDelete: () => {
        setStartDate("");
        setEndDate("");
      },
    });
  if (statusFilter !== DEFAULT_STATUS)
    chips.push({
      label: statusFilter,
      onDelete: () => setStatusFilter(DEFAULT_STATUS),
    });

  const locationLabelId = "booking-location-label";

  return (
    <Paper
      variant="outlined"
      sx={{ p: isCompact ? 1.25 : 2, mb: isCompact ? 1 : 2, borderRadius: 2 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", md: "center" }}
        useFlexGap
        flexWrap="wrap">
        <TextField
          inputRef={searchRef}
          label="Search"
          size="small"
          value={searchQuery}
          placeholder="name/email/order id"
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ ...controlMinWidth(260), flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl
          size="small"
          sx={controlMinWidth(170)}
          fullWidth={isMobile}>
          {/* force-shrink so it sits in the notch and never overlaps */}
          <InputLabel id={locationLabelId} shrink>
            Location
          </InputLabel>
          <Select
            labelId={locationLabelId}
            label="Location"
            value={locationFilter ?? DEFAULT_LOCATION}
            onChange={(e) => setLocationFilter(e.target.value)}
            displayEmpty
            renderValue={(val) => (val ? val : <span>All Locations</span>)}>
            <MenuItem value={DEFAULT_LOCATION}>
              <em>All Locations</em>
            </MenuItem>
            {locations.map((loc) => (
              <MenuItem key={String(loc)} value={loc}>
                {loc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          sx={controlMinWidth(180)}
          fullWidth={isMobile}>
          <InputLabel>Date filter on</InputLabel>
          <Select
            label="Date filter on"
            value={dateOn}
            onChange={(e) => setDateOn(e.target.value)}>
            <MenuItem value="pickup">Pickup date</MenuItem>
            <MenuItem value="order">Order date</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="From"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          sx={controlMinWidth(150)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          sx={controlMinWidth(150)}
          InputLabelProps={{ shrink: true }}
        />

        <FormControl
          size="small"
          sx={controlMinWidth(160)}
          fullWidth={isMobile}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value={DEFAULT_STATUS}>in-progress</MenuItem>
            {statuses
              .filter((s) => s !== DEFAULT_STATUS)
              .map((s) => (
                <MenuItem key={String(s)} value={s}>
                  {s}
                </MenuItem>
              ))}
            <MenuItem value="">
              <em>All Statuses</em>
            </MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {hasFilters && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ width: "100%", pt: 1, flexWrap: "wrap", alignItems: "center" }}>
          {chips.map((c, i) => (
            <Chip key={i} size="small" label={c.label} onDelete={c.onDelete} />
          ))}
          <Box sx={{ ml: "auto" }}>
            <Button
              size="small"
              variant="text"
              color="secondary"
              onClick={clearFilters}
              sx={{ textTransform: "none" }}>
              Clear all
            </Button>
          </Box>
        </Stack>
      )}
    </Paper>
  );
};

export default BookingFilters;
